import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Menu, Phone, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LanguageSelector } from "./LanguageSelector";
import { Wordmark } from "./Wordmark";
import { localizePath, type LocaleCode, useI18n } from "@/i18n";
import { business } from "@/config/business";
import {
  publishedServicePages,
  SERVICE_CATEGORY_LABELS,
  SERVICE_CATEGORY_ORDER,
  type ServiceCategoryId,
} from "@/config/services";
import { localizeServicePage } from "@/i18n/content";
import { cn } from "@/lib/utils";
import { trackAnalyticsEvent } from "@/lib/analytics";
import { accessibilityCopy } from "@/i18n/accessibility";

type NavigationCopy = {
  services: string;
  allServices: string;
  emergency: string;
  residential: string;
  commercial: string;
  strata: string;
  roofSystems: string;
  serviceAreas: string;
  projects: string;
  about: string;
  estimate: string;
  call: string;
};

const NAVIGATION_COPY: Record<LocaleCode, NavigationCopy> = {
  "en-ca": {
    services: "Services",
    allServices: "View all roofing services",
    emergency: "Emergency",
    residential: "Residential",
    commercial: "Commercial",
    strata: "Strata",
    roofSystems: "Roof Systems",
    serviceAreas: "Service Areas",
    projects: "Projects",
    about: "About",
    estimate: "Get Estimate",
    call: "Call Revolin Roofing",
  },
  "zh-cn": {
    services: "服务项目",
    allServices: "查看全部屋顶服务",
    emergency: "紧急服务",
    residential: "住宅屋顶",
    commercial: "商业屋顶",
    strata: "共管物业",
    roofSystems: "屋顶系统",
    serviceAreas: "服务区域",
    projects: "项目案例",
    about: "关于我们",
    estimate: "获取估价",
    call: "致电 Revolin Roofing",
  },
  "zh-tw": {
    services: "服務項目",
    allServices: "查看全部屋頂服務",
    emergency: "緊急服務",
    residential: "住宅屋頂",
    commercial: "商業屋頂",
    strata: "共管物業",
    roofSystems: "屋頂系統",
    serviceAreas: "服務區域",
    projects: "工程案例",
    about: "關於我們",
    estimate: "取得估價",
    call: "致電 Revolin Roofing",
  },
  "pa-in": {
    services: "ਸੇਵਾਵਾਂ",
    allServices: "ਸਾਰੀਆਂ ਛੱਤ ਸੇਵਾਵਾਂ ਵੇਖੋ",
    emergency: "ਐਮਰਜੈਂਸੀ",
    residential: "ਰਿਹਾਇਸ਼ੀ",
    commercial: "ਵਪਾਰਕ",
    strata: "ਸਟ੍ਰਾਟਾ",
    roofSystems: "ਛੱਤ ਪ੍ਰਣਾਲੀਆਂ",
    serviceAreas: "ਸੇਵਾ ਖੇਤਰ",
    projects: "ਪ੍ਰੋਜੈਕਟ",
    about: "ਸਾਡੇ ਬਾਰੇ",
    estimate: "ਅੰਦਾਜ਼ਾ ਲਵੋ",
    call: "Revolin Roofing ਨੂੰ ਕਾਲ ਕਰੋ",
  },
  "ko-kr": {
    services: "서비스",
    allServices: "전체 지붕 서비스 보기",
    emergency: "긴급 서비스",
    residential: "주택",
    commercial: "상업용",
    strata: "공동주택",
    roofSystems: "지붕 시스템",
    serviceAreas: "서비스 지역",
    projects: "시공 사례",
    about: "회사 소개",
    estimate: "견적 받기",
    call: "Revolin Roofing에 전화",
  },
  "fa-ir": {
    services: "خدمات",
    allServices: "مشاهدهٔ همهٔ خدمات سقف",
    emergency: "خدمات اضطراری",
    residential: "سقف مسکونی",
    commercial: "سقف تجاری",
    strata: "مجتمع‌های مسکونی",
    roofSystems: "سیستم‌های سقف",
    serviceAreas: "مناطق خدماتی",
    projects: "پروژه‌ها",
    about: "دربارهٔ ما",
    estimate: "دریافت برآورد",
    call: "تماس با Revolin Roofing",
  },
  "ar-sa": {
    services: "الخدمات",
    allServices: "عرض جميع خدمات الأسقف",
    emergency: "خدمات الطوارئ",
    residential: "الأسقف السكنية",
    commercial: "الأسقف التجارية",
    strata: "المجمعات السكنية",
    roofSystems: "أنظمة الأسقف",
    serviceAreas: "مناطق الخدمة",
    projects: "المشاريع",
    about: "من نحن",
    estimate: "اطلب تقديراً",
    call: "اتصل بـ Revolin Roofing",
  },
};

const CATEGORY_COPY: Partial<Record<LocaleCode, Partial<Record<ServiceCategoryId, string>>>> = {
  "zh-cn": {
    "emergency-repairs": "紧急与维修",
    replacement: "屋顶更换",
    maintenance: "维护保养",
    "gutters-exterior": "排水槽与外部组件",
  },
  "zh-tw": {
    "emergency-repairs": "緊急與維修",
    replacement: "屋頂更換",
    maintenance: "維護保養",
    "gutters-exterior": "排水槽與外部組件",
  },
  "pa-in": {
    "emergency-repairs": "ਐਮਰਜੈਂਸੀ ਅਤੇ ਮੁਰੰਮਤ",
    replacement: "ਛੱਤ ਬਦਲਣਾ",
    maintenance: "ਰੱਖ-ਰਖਾਅ",
    "gutters-exterior": "ਗਟਰ ਅਤੇ ਬਾਹਰੀ ਹਿੱਸੇ",
  },
  "ko-kr": {
    "emergency-repairs": "긴급 및 수리",
    replacement: "지붕 교체",
    maintenance: "유지관리",
    "gutters-exterior": "홈통 및 외장 구성요소",
  },
  "fa-ir": {
    "emergency-repairs": "خدمات اضطراری و تعمیرات",
    replacement: "تعویض سقف",
    maintenance: "نگهداری سقف",
    "gutters-exterior": "ناودان و اجزای بیرونی",
  },
  "ar-sa": {
    "emergency-repairs": "الطوارئ والإصلاح",
    replacement: "استبدال السقف",
    maintenance: "صيانة السقف",
    "gutters-exterior": "المزاريب والمكونات الخارجية",
  },
};

const SEGMENT_LINKS: readonly { href: string; key: keyof NavigationCopy; service?: string }[] = [
  { href: "/emergency-roofing", key: "emergency", service: "emergency_roofing" },
  { href: "/residential-roofing", key: "residential" },
  { href: "/commercial-roofing", key: "commercial" },
  { href: "/strata-roofing", key: "strata" },
  { href: "/roof-systems", key: "roofSystems" },
  { href: "/service-areas", key: "serviceAreas" },
  { href: "/before-after", key: "projects" },
  { href: "/about", key: "about" },
] as const;

export const NAV_LINKS = [
  { href: "/services", key: "services" },
  ...SEGMENT_LINKS.map((link) => ({ href: link.href, key: link.key })),
] as const;

export function Header() {
  const { t, locale } = useI18n();
  const copy = NAVIGATION_COPY[locale];
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const firstMobileLinkRef = useRef<HTMLElement>(null);
  const serviceGroups = useMemo(
    () =>
      SERVICE_CATEGORY_ORDER.map((category) => ({
        category,
        services: publishedServicePages
          .filter((service) => service.category === category)
          .map((service) => localizeServicePage(service, locale)),
      })).filter((group) => group.services.length > 0),
    [locale],
  );

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && open) {
        setOpen(false);
        window.requestAnimationFrame(() => menuButtonRef.current?.focus());
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (open) firstMobileLinkRef.current?.focus();
  }, [open]);

  const categoryLabel = (category: ServiceCategoryId) =>
    CATEGORY_COPY[locale]?.[category] ?? SERVICE_CATEGORY_LABELS[category];

  const trackSegment = (service?: string) => {
    if (!service) return;
    trackAnalyticsEvent({
      event: "service_cta_click",
      cta_location: "site_navigation",
      service,
    });
  };

  return (
    <header
      className={cn(
        "fixed inset-x-0 top-0 z-50 border-b text-primary-foreground transition-all duration-300",
        scrolled || open
          ? "border-accent/25 bg-primary/98 shadow-elev backdrop-blur"
          : "border-accent/15 bg-primary/94 backdrop-blur-sm",
      )}
    >
      <div className="container-page grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 py-3 lg:py-4">
        <Wordmark onDark className="min-w-0 shrink-0" />

        <div className="flex items-center gap-1">
          <nav
            aria-label={accessibilityCopy[locale].primaryNavigation}
            className="hidden items-center min-[1420px]:flex"
          >
            <details className="group relative">
              <summary className="flex cursor-pointer list-none items-center gap-1 rounded-md px-2 py-2 text-[0.72rem] font-semibold text-primary-foreground/78 transition-colors hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent [&::-webkit-details-marker]:hidden">
                {copy.services}
                <ChevronDown
                  aria-hidden="true"
                  className="size-3.5 transition-transform group-open:rotate-180"
                />
              </summary>
              <div className="fixed inset-x-0 top-[4.95rem] border-y border-accent/20 bg-primary/99 shadow-elev backdrop-blur-xl">
                <div className="container-page max-h-[calc(100dvh-7rem)] overflow-y-auto py-7">
                  <div className="mb-6 flex items-end justify-between gap-6 border-b border-primary-foreground/12 pb-4">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">
                        {copy.services}
                      </p>
                      <p className="mt-1 text-sm text-primary-foreground/55">
                        {publishedServicePages.length} {copy.services.toLocaleLowerCase()}
                      </p>
                    </div>
                    <a
                      href={localizePath("/services", locale)}
                      className="text-sm font-semibold text-primary-foreground underline decoration-accent/60 underline-offset-4 hover:text-accent"
                    >
                      {copy.allServices}
                    </a>
                  </div>
                  <div className="grid grid-cols-2 gap-x-8 gap-y-8 lg:grid-cols-4">
                    {serviceGroups.map((group) => (
                      <section key={group.category} aria-labelledby={`services-${group.category}`}>
                        <h2
                          id={`services-${group.category}`}
                          className="mb-3 text-[0.7rem] font-bold uppercase tracking-[0.16em] text-accent"
                        >
                          {categoryLabel(group.category)}
                        </h2>
                        <ul className="space-y-2.5">
                          {group.services.map((service) => (
                            <li key={service.slug}>
                              <a
                                href={localizePath(service.path, locale)}
                                className="block text-[0.78rem] leading-snug text-primary-foreground/70 transition-colors hover:text-primary-foreground"
                              >
                                {service.shortTitle}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </section>
                    ))}
                  </div>
                </div>
              </div>
            </details>

            {SEGMENT_LINKS.map((link) => (
              <a
                key={link.key}
                href={localizePath(link.href, locale)}
                onClick={() => trackSegment(link.service)}
                className={cn(
                  "rounded-md px-2 py-2 text-[0.72rem] font-medium text-primary-foreground/70 transition-colors hover:text-accent",
                  link.service && "text-accent",
                )}
              >
                {copy[link.key]}
              </a>
            ))}
          </nav>

          <span
            className="mx-1 hidden h-5 w-px bg-primary-foreground/15 min-[1420px]:block"
            aria-hidden="true"
          />
          <LanguageSelector onDark />

          <Button variant="accent" size="pill" asChild className="hidden lg:inline-flex lg:px-5">
            <a
              href={localizePath("/roof-estimate", locale)}
              onClick={() =>
                trackAnalyticsEvent({
                  event: "service_cta_click",
                  cta_location: "site_header",
                  service: "general_roofing",
                })
              }
            >
              {copy.estimate}
            </a>
          </Button>

          <Button
            ref={menuButtonRef}
            variant="ghost"
            size="icon"
            className="text-primary-foreground hover:bg-primary-foreground/10 hover:text-accent min-[1420px]:hidden"
            aria-expanded={open}
            aria-controls="mobile-nav"
            aria-label={open ? t.nav.close : t.nav.menu}
            onClick={() => setOpen((value) => !value)}
          >
            {open ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
          </Button>
        </div>
      </div>

      {open ? (
        <div
          id="mobile-nav"
          className="max-h-[calc(100dvh-4.5rem)] overflow-y-auto border-t border-accent/20 bg-primary min-[1420px]:hidden"
        >
          <nav
            aria-label={accessibilityCopy[locale].mobileNavigation}
            className="container-page flex flex-col py-2"
          >
            <details className="group border-b border-primary-foreground/10">
              <summary
                ref={firstMobileLinkRef}
                className="flex cursor-pointer list-none items-center justify-between py-4 text-base font-semibold text-primary-foreground hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent [&::-webkit-details-marker]:hidden"
              >
                {copy.services}
                <ChevronDown
                  aria-hidden="true"
                  className="size-4 transition-transform group-open:rotate-180"
                />
              </summary>
              <div className="pb-5">
                <a
                  href={localizePath("/services", locale)}
                  onClick={() => setOpen(false)}
                  className="mb-5 block text-sm font-semibold text-accent underline underline-offset-4"
                >
                  {copy.allServices}
                </a>
                <div className="grid gap-6 sm:grid-cols-2">
                  {serviceGroups.map((group) => (
                    <section key={group.category} aria-labelledby={`mobile-${group.category}`}>
                      <h2
                        id={`mobile-${group.category}`}
                        className="mb-2 text-[0.68rem] font-bold uppercase tracking-[0.14em] text-accent"
                      >
                        {categoryLabel(group.category)}
                      </h2>
                      <ul className="space-y-1">
                        {group.services.map((service) => (
                          <li key={service.slug}>
                            <a
                              href={localizePath(service.path, locale)}
                              onClick={() => setOpen(false)}
                              className="block py-1.5 text-sm leading-snug text-primary-foreground/70 hover:text-primary-foreground"
                            >
                              {service.shortTitle}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </section>
                  ))}
                </div>
              </div>
            </details>

            {SEGMENT_LINKS.map((link) => (
              <a
                key={link.key}
                href={localizePath(link.href, locale)}
                onClick={() => {
                  trackSegment(link.service);
                  setOpen(false);
                }}
                className={cn(
                  "border-b border-primary-foreground/10 py-4 text-base font-medium text-primary-foreground hover:text-accent",
                  link.service && "text-accent",
                )}
              >
                {copy[link.key]}
              </a>
            ))}
            <div className="grid gap-2 py-4 sm:grid-cols-2">
              <Button variant="accent" size="xl" asChild>
                <a
                  href={localizePath("/roof-estimate", locale)}
                  onClick={() => {
                    trackAnalyticsEvent({
                      event: "service_cta_click",
                      cta_location: "mobile_menu",
                      service: "general_roofing",
                    });
                    setOpen(false);
                  }}
                >
                  {copy.estimate}
                </a>
              </Button>
              <Button variant="quiet" size="xl" asChild>
                <a
                  href={business.phoneHref}
                  onClick={() =>
                    trackAnalyticsEvent({ event: "phone_click", link_location: "mobile_menu" })
                  }
                >
                  <Phone aria-hidden="true" /> {copy.call}
                </a>
              </Button>
            </div>
          </nav>
        </div>
      ) : null}
    </header>
  );
}
